/* eslint-disable react-native/no-inline-styles */
import React, { useState, useCallback, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    RefreshControl,
    Modal,
    TouchableWithoutFeedback,
    Platform,
    ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import ApiService from '../services/ApiService';
import { ReportStats, ReportPeriod, DairyRecord } from '../models/types';
import { formatNumber, formatCurrency } from '../utils/helpers';
import {
    SkeletonCard,
    SkeletonDetailCard,
} from '../components/ShimmerLoading';
import { SummaryListCard, SummaryListItem } from '../components/SummaryListCard';
import { NetProfitCard } from '../components/NetProfitCard';
import { DatePickerInput } from '../components/DatePickerInput';

interface DairyTotals {
    purchaseQty: number;
    purchaseAmount: number;
    saleQty: number;
    saleAmount: number;
    salePendingAmount: number;
}

const SkeletonReportLoading = () => (
    <View style={{ flex: 1 }}>
        {/* Tabs Skeleton */}
        {/* <View style={styles.tabWrapper}>
            <View style={styles.tabContainer}>
                {[1, 2, 3].map((_, index) => (
                    <View
                        key={index}
                        style={[
                            styles.tab,
                            index === 0 && styles.tabActive,
                            { backgroundColor: index === 0 ? theme.colors.primary : theme.colors.background },
                        ]}
                    />
                ))}
            </View>
        </View> */}

        {/* Date Selector Skeleton */}
        {/* <View style={styles.dateSelectorWrapper}>
            <View style={styles.dateSelectorCard}>
                <View style={styles.dateNavButton} />
                <View style={styles.dateDisplay}>
                    <View style={styles.dateIconContainer} />
                    <View style={{ flex: 1, height: 16, backgroundColor: '#E1E9EE', borderRadius: 4 }} />
                </View>
                <View style={styles.dateNavButton} />
            </View>
        </View> */}

        {/* Content Cards */}
        <ScrollView
            style={styles.scrollView}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonDetailCard />
            <View style={styles.bottomPadding} />
        </ScrollView>
    </View>
);

// Content-only skeleton for when tabs are already visible
const SkeletonContentLoading = () => (
    <>
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonDetailCard />
        <View style={styles.bottomPadding} />
    </>
);

interface StatItem {
    icon: string;
    iconColor: string;
    iconBgColor: string;
    label: string;
    value: string;
    valueColor?: string;
}

interface StatsSummaryCardProps {
    title: string;
    leftStat: StatItem;
    rightStat: StatItem;
}

const StatsSummaryCard = ({ title, leftStat, rightStat }: StatsSummaryCardProps) => (
    <View style={styles.card}>
        <Text style={styles.cardTitle}>{title}</Text>
        <View style={styles.cardRow}>
            <View style={styles.cardItem}>
                <View style={[styles.cardIconContainer, { backgroundColor: leftStat.iconBgColor }]}>
                    <Icon name={leftStat.icon} size={22} color={leftStat.iconColor} />
                </View>
                <Text style={styles.cardLabel}>{leftStat.label}</Text>
                <Text style={[styles.cardValue, leftStat.valueColor && { color: leftStat.valueColor }]}>
                    {leftStat.value}
                </Text>
            </View>

            <View style={styles.cardDivider} />

            <View style={styles.cardItem}>
                <View style={[styles.cardIconContainer, { backgroundColor: rightStat.iconBgColor }]}>
                    <Icon name={rightStat.icon} size={22} color={rightStat.iconColor} />
                </View>
                <Text style={styles.cardLabel}>{rightStat.label}</Text>
                <Text style={[styles.cardValue, rightStat.valueColor && { color: rightStat.valueColor }]}>
                    {rightStat.value}
                </Text>
            </View>
        </View>
    </View>
);

const ReportsScreen = () => {
    const [period, setPeriod] = useState<ReportPeriod>('daily');
    const [selectedDate, setSelectedDate] = useState(new Date());
    // Bottom sheet filter state
    const [filterVisible, setFilterVisible] = useState(false);
    const [applyingFilter, setApplyingFilter] = useState(false);
    const [sheetPeriod, setSheetPeriod] = useState<ReportPeriod>('daily');
    const [sheetFromDate, setSheetFromDate] = useState<string>('');
    const [sheetToDate, setSheetToDate] = useState<string>('');
    const [selectedRange, setSelectedRange] = useState<{ from: string; to: string } | null>(null);
    const [stats, setStats] = useState<ReportStats | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [dairyTotals, setDairyTotals] = useState<DairyTotals>({
        purchaseQty: 0,
        purchaseAmount: 0,
        saleQty: 0,
        saleAmount: 0,
        salePendingAmount: 0,
    });

    // Helper function to format date as YYYY-MM-DD without timezone shift
    const formatDateToString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getDateRange = (date: Date, periodType: ReportPeriod): { from: string; to: string } => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();

        switch (periodType) {
            case 'daily':
                const dailyDate = formatDateToString(new Date(year, month, day));
                return { from: dailyDate, to: dailyDate };
            case 'weekly':
                const dayOfWeek = date.getDay();
                const startOfWeek = new Date(year, month, day - dayOfWeek);
                const endOfWeek = new Date(year, month, day + (6 - dayOfWeek));
                return {
                    from: formatDateToString(startOfWeek),
                    to: formatDateToString(endOfWeek),
                };
            case 'monthly':
                const startOfMonth = new Date(year, month, 1);
                const endOfMonth = new Date(year, month + 1, 0);
                return {
                    from: formatDateToString(startOfMonth),
                    to: formatDateToString(endOfMonth),
                };
            default:
                const defaultDate = formatDateToString(date);
                return { from: defaultDate, to: defaultDate };
        }
    };

    const formatDateRange = (from: string, to: string): string => {
        // Parse dates in local timezone by adding time component
        const [fromYear, fromMonth, fromDay] = from.split('-').map(Number);
        const [toYear, toMonth, toDay] = to.split('-').map(Number);
        const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
        const toDate = new Date(toYear, toMonth - 1, toDay);
        const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

        if (from === to) {
            return fromDate.toLocaleDateString('en-US', options);
        }
        return `${fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${toDate.toLocaleDateString('en-US', options)}`;
    };

    const loadReportData = async (showLoader = true) => {
        try {
            if (showLoader && initialLoading) {
                setLoading(true);
            }
            const range = selectedRange ?? getDateRange(selectedDate, period);
            const { from, to } = range;
            const reportStats = await ApiService.getReportStats(from, to);
            setStats(reportStats);

            // Load dairy records for the selected period
            const dairyRecords = await ApiService.getDairyRecords();
            const totals: DairyTotals = {
                purchaseQty: 0,
                purchaseAmount: 0,
                saleQty: 0,
                saleAmount: 0,
                salePendingAmount: 0,
            };

            // Filter dairy records by date range
            dairyRecords.forEach((record: DairyRecord) => {
                const recordDate = record.date.split('T')[0];
                if (recordDate >= from && recordDate <= to) {
                    if (record.recordType === 'purchase') {
                        totals.purchaseQty += record.quantity;
                        totals.purchaseAmount += record.totalAmount || 0;
                    } else if (record.recordType === 'sale') {
                        totals.saleQty += record.quantity;
                        totals.saleAmount += record.totalAmount || 0;
                        totals.salePendingAmount += record.pendingAmount || 0;
                    }
                }
            });

            setDairyTotals(totals);
            // Don't persist selectedRange here - it should only be set from filter
        } catch (error) {
            console.error('Error loading report data:', error);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    const onRefresh = async () => {
        setRefreshing(true);
        await loadReportData();
        setRefreshing(false);
    };

    useFocusEffect(
        useCallback(() => {
            // Reset to today's date when screen is focused
            setSelectedDate(new Date());
        }, []),
    );

    // Load data when selectedDate or period changes
    useEffect(() => {
        loadReportData();
    }, [selectedDate, period, selectedRange]);

    const handlePeriodChange = (newPeriod: ReportPeriod) => {
        setLoading(true); // Show shimmer when changing period
        setPeriod(newPeriod);
    };

    // Header filter icon handlers
    const openFilterSheet = () => {
        setSheetPeriod(period);
        // Clear date inputs so they're optional unless user explicitly sets them
        setSheetFromDate('');
        setSheetToDate('');
        setFilterVisible(true);
    };
    const applyFilterSheet = async () => {
        setApplyingFilter(true);
        setLoading(true);
        
        // If user provided custom dates, use them
        // Otherwise, use period-based calculation from today
        const hasCustomDates = sheetFromDate || sheetToDate;
        
        if (hasCustomDates) {
            // Custom date range mode
            let finalFrom = sheetFromDate;
            let finalTo = sheetToDate;

            const computePeriodBounds = (baseStr: string, p: ReportPeriod) => {
                const base = new Date(baseStr);
                const year = base.getFullYear();
                const month = base.getMonth();
                const day = base.getDate();
                if (p === 'daily') {
                    const s = new Date(year, month, day);
                    return { from: formatDateToString(s), to: formatDateToString(s) };
                }
                if (p === 'weekly') {
                    const dayOfWeek = base.getDay();
                    const start = new Date(year, month, day - dayOfWeek);
                    const end = new Date(year, month, day + (6 - dayOfWeek));
                    return { from: formatDateToString(start), to: formatDateToString(end) };
                }
                const start = new Date(year, month, 1);
                const end = new Date(year, month + 1, 0);
                return { from: formatDateToString(start), to: formatDateToString(end) };
            };

            if (finalFrom && !finalTo) {
                const bounds = computePeriodBounds(finalFrom, sheetPeriod);
                finalTo = bounds.to;
            } else if (!finalFrom && finalTo) {
                const bounds = computePeriodBounds(finalTo, sheetPeriod);
                finalFrom = bounds.from;
            }

            setSelectedRange({ from: finalFrom, to: finalTo });
            setPeriod(sheetPeriod);
        } else {
            // Period-based mode - use today with selected period
            setPeriod(sheetPeriod);
            setSelectedDate(new Date());
            setSelectedRange(null); // Clear custom range
        }
        
        setFilterVisible(false);
        
        // Wait a small moment for the state to update, then clear applying state
        setTimeout(() => {
            setApplyingFilter(false);
        }, 100);
    };

    const navigateDate = (direction: 'prev' | 'next') => {
        const newDate = new Date(selectedDate);
        switch (period) {
            case 'daily':
                newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
                break;
            case 'weekly':
                newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
                break;
            case 'monthly':
                newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
                break;
        }
        setSelectedDate(newDate);
    };

    const renderPeriodTabs = () => (
        <View style={styles.tabWrapper}>
            {([
                { key: 'daily', label: 'Daily' },
                { key: 'weekly', label: 'Weekly' },
                { key: 'monthly', label: 'Monthly' },
            ] as { key: ReportPeriod; label: string }[]).map((p) => {
                const isActive = period === p.key;
                return (
                    <TouchableOpacity
                        key={p.key}
                        style={[
                            styles.tab,
                            isActive && styles.tabActive,
                        ]}
                        onPress={() => handlePeriodChange(p.key)}
                        activeOpacity={0.7}>
                        <Text style={[styles.tabText, isActive && styles.tabTextActive]}>
                            {p.label}
                        </Text>
                    </TouchableOpacity>
                );
            })}
        </View>
    );

    const renderMilkSection = () => {
        if (!stats) return null;
        return (
            <StatsSummaryCard
                title="Milk Production"
                leftStat={{
                    icon: 'cup',
                    iconColor: theme.colors.primary,
                    iconBgColor: theme.colors.primary + '20',
                    label: 'Produced',
                    value: `${formatNumber(stats.milkProduced)} L`,
                }}
                rightStat={{
                    icon: 'cash-multiple',
                    iconColor: theme.colors.success,
                    iconBgColor: theme.colors.success + '20',
                    label: 'Sold',
                    value: `${formatNumber(stats.milkSold)} L`,
                    valueColor: theme.colors.success,
                }}
            />
        );
    };

    const renderProfitSection = () => {
        if (!stats) return null;
        const isProfit = stats.profit >= 0;
        const profitColor = isProfit ? theme.colors.success : theme.colors.error;
        return (
            <StatsSummaryCard
                title="Revenue & Profit"
                leftStat={{
                    icon: 'currency-inr',
                    iconColor: theme.colors.primary,
                    iconBgColor: theme.colors.primary + '20',
                    label: 'Revenue',
                    value: formatCurrency(stats.totalRevenue),
                    valueColor: theme.colors.primary,
                }}
                rightStat={{
                    icon: isProfit ? 'trending-up' : 'trending-down',
                    iconColor: profitColor,
                    iconBgColor: profitColor + '20',
                    label: isProfit ? 'Profit' : 'Loss',
                    value: formatCurrency(Math.abs(stats.profit)),
                    valueColor: profitColor,
                }}
            />
        );
    };

    const renderExpensesSection = () => {
        if (!stats) return null;
        const expensesItems: SummaryListItem[] = [
            {
                icon: 'grass',
                iconColor: theme.colors.feed,
                iconBgColor: theme.colors.feed + '20',
                label: 'Fodder Cost',
                value: formatCurrency(stats.fodderCost),
            },
            {
                icon: 'medical-bag',
                iconColor: theme.colors.medicine,
                iconBgColor: theme.colors.medicine + '20',
                label: 'Medicine Cost',
                value: formatCurrency(stats.medicineCost),
            },
            {
                icon: 'cup',
                iconColor: theme.colors.primary,
                iconBgColor: theme.colors.primary + '20',
                label: 'Milk Production',
                value: `${formatNumber(stats.milkProduced)} L`,
            },
        ];
        return <SummaryListCard title="Expenses Summary" items={expensesItems} />;
    };
    const renderDairyRecordsSection = () => {
        const dairyItems: SummaryListItem[] = [
            {
                icon: 'cart-plus',
                iconColor: '#FF9800',
                iconBgColor: '#FFF3E0',
                label: 'Purchases',
                value: `${formatNumber(dairyTotals.purchaseQty)} L`,
                subValue: formatCurrency(dairyTotals.purchaseAmount),
            },
            {
                icon: 'cash-multiple',
                iconColor: '#4CAF50',
                iconBgColor: '#E8F5E9',
                label: 'Sales',
                value: `${formatNumber(dairyTotals.saleQty)} L`,
                valueColor: theme.colors.success,
                subValue: formatCurrency(dairyTotals.saleAmount),
                subValueColor: theme.colors.success,
            },
        ];
        return <SummaryListCard title="Dairy Records" items={dairyItems} />;
    };

    const renderMilkStockSection = () => {
        if (!stats) return null;
        const totalMilkAvailable = stats.milkProduced + dairyTotals.purchaseQty;
        const remainingMilk = totalMilkAvailable - dairyTotals.saleQty;
        const stockColor = remainingMilk >= 0 ? '#2196F3' : theme.colors.error;

        const milkStockItems: SummaryListItem[] = [
            {
                icon: 'cup',
                iconColor: theme.colors.primary,
                iconBgColor: theme.colors.primary + '20',
                label: 'Produced by Cows',
                value: `${formatNumber(stats.milkProduced)} L`,
            },
            {
                icon: 'cart-plus',
                iconColor: '#FF9800',
                iconBgColor: '#FFF3E0',
                label: 'Purchased',
                value: `+${formatNumber(dairyTotals.purchaseQty)} L`,
            },
            {
                icon: 'cart-minus',
                iconColor: theme.colors.error,
                iconBgColor: '#FFEBEE',
                label: 'Sold',
                value: `-${formatNumber(dairyTotals.saleQty)} L`,
                valueColor: theme.colors.error,
            },
            {
                icon: 'water',
                iconColor: stockColor,
                iconBgColor: remainingMilk >= 0 ? '#E3F2FD' : '#FFEBEE',
                label: 'Remaining Stock',
                value: `${formatNumber(remainingMilk)} L`,
                valueColor: stockColor,
                labelStyle: { fontWeight: '700' },
                valueStyle: { fontWeight: '700', fontSize: 16 },
            },
        ];
        return <SummaryListCard title="Milk Stock Summary" items={milkStockItems} />;
    };

    const renderNetProfitSection = () => {
        if (!stats) return null;
        // Revenue = Sales only
        const totalRevenue = dairyTotals.saleAmount;
        const totalExpensesCombined = stats.totalExpenses;
        const netProfit = totalRevenue - totalExpensesCombined;
        const pendingAmount = dairyTotals.salePendingAmount;
        const realizedProfit = (totalRevenue - pendingAmount) - totalExpensesCombined;
        const subLabel = period === 'daily' ? 'Today' : period === 'weekly' ? 'This Week' : 'This Month';
        
        return (
            <NetProfitCard
                netProfit={netProfit}
                totalRevenue={totalRevenue}
                totalExpenses={totalExpensesCombined}
                pendingAmount={pendingAmount}
                realizedProfit={realizedProfit}
                subLabel={subLabel}
            />
        );
    };

    const renderCowPerformance = () => {
        if (!stats || stats.perCowPerformance.length === 0) return null;
        return (
            <View style={styles.card}>
                <Text style={styles.cardTitle}>Per Cow Performance</Text>
                {stats.perCowPerformance.map((cow, index) => (
                    <View key={cow.cowId} style={styles.cowCard}>
                        <View style={styles.cowHeader}>
                            <View style={styles.cowInfo}>
                                <Icon name="cow" size={24} color={theme.colors.primary} />
                                <Text style={styles.cowName}>{cow.name}</Text>
                            </View>
                            <View style={styles.cowMilk}>
                                <Text style={styles.cowMilkValue}>{formatNumber(cow.milkProduction)}</Text>
                                <Text style={styles.cowMilkUnit}>L</Text>
                            </View>
                        </View>
                        <View style={styles.cowStats}>
                            <View style={styles.cowStatItem}>
                                <Icon name="medical-bag" size={16} color={theme.colors.medicine} />
                                <Text style={styles.cowStatLabel}>Medicine</Text>
                                <Text style={styles.cowStatValue}>{formatCurrency(cow.medicineCost)}</Text>
                            </View>
                            <View style={styles.cowStatItem}>
                                <Icon name="grass" size={16} color={theme.colors.feed} />
                                <Text style={styles.cowStatLabel}>Fodder</Text>
                                <Text style={styles.cowStatValue}>{formatCurrency(cow.fodderCost)}</Text>
                            </View>
                            {cow.milkRevenue > 0 && (
                                <View style={styles.cowStatItem}>
                                    <Icon name="cash" size={16} color={theme.colors.success} />
                                    <Text style={styles.cowStatLabel}>Revenue</Text>
                                    <Text style={[styles.cowStatValue, { color: theme.colors.success }]}>
                                        {formatCurrency(cow.milkRevenue)}
                                    </Text>
                                </View>
                            )}
                        </View>
                    </View>
                ))}
            </View>
        );
    };

    const renderEmptyState = () => (
        <View style={styles.emptyState}>
            <Icon name="chart-bar" size={64} color={theme.colors.textLight} />
            <Text style={styles.emptyTitle}>No Data Available</Text>
            <Text style={styles.emptySubtitle}>
                No records found for the selected period.{'\n'}
                Try selecting a different date range.
            </Text>
        </View>
    );

    const hasDairyData = dairyTotals.purchaseQty > 0 || dairyTotals.saleQty > 0;
    const hasData = stats && (stats.milkProduced > 0 || stats.milkSold > 0 || stats.totalRevenue > 0 || stats.perCowPerformance.length > 0 || hasDairyData);

    return (
        <>
            <SafeAreaView style={styles.container}>
                <View style={styles.header}>
                    <View style={styles.headerPlaceholder} />
                    <Text style={styles.title}>Reports</Text>
                    <TouchableOpacity
                        style={styles.headerAction}
                        onPress={openFilterSheet}
                        activeOpacity={0.7}
                    >
                        <Icon name="filter-variant" size={24} color={theme.colors.surface} />
                    </TouchableOpacity>
                </View>

                {stats && (
                    <View style={styles.rangeInfoWrapper}>
                        <Icon name="calendar-range" size={16} color={theme.colors.primary} />
                        <Text style={styles.rangeInfoText}>
                            Showing: {formatDateRange(stats.dateRange.from, stats.dateRange.to)}
                        </Text>
                    </View>
                )}

                {initialLoading ? (
                    <SkeletonReportLoading />
                ) : (
                    <>
                        {/* Tabs hidden from page per requirement; available inside filter sheet */}

                        <ScrollView
                            style={styles.scrollView}
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={styles.scrollContent}
                            refreshControl={
                                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                            }>
                            {loading ? (
                                <SkeletonContentLoading />
                            ) : hasData ? (
                                <>
                                    {renderMilkSection()}
                                    {renderProfitSection()}
                                    {renderExpensesSection()}
                                    {renderDairyRecordsSection()}
                                    {renderMilkStockSection()}
                                    {renderNetProfitSection()}
                                    {renderCowPerformance()}
                                </>
                            ) : (
                                renderEmptyState()
                            )}
                            <View style={styles.bottomPadding} />
                        </ScrollView>
                    </>
                )}
            </SafeAreaView>

            {/* Bottom Sheet Filter Modal */}
            <Modal
                visible={filterVisible}
                transparent
                animationType="slide"
                onRequestClose={() => setFilterVisible(false)}
                presentationStyle="overFullScreen"
                statusBarTranslucent
            >
                <TouchableWithoutFeedback onPress={() => setFilterVisible(false)}>
                    <View style={styles.sheetOverlay}>
                        <TouchableWithoutFeedback>
                            <View style={styles.bottomSheet}>
                                <View style={styles.sheetHandle} />
                                <Text style={styles.sheetTitle}>Filters</Text>

                                <Text style={styles.sheetSectionLabel}>Period</Text>
                                <View style={styles.sheetTabsRow}>
                                    {([
                                        { key: 'daily', label: 'Daily' },
                                        { key: 'weekly', label: 'Weekly' },
                                        { key: 'monthly', label: 'Monthly' },
                                    ] as { key: ReportPeriod; label: string }[]).map((p) => {
                                        const isActive = sheetPeriod === p.key;
                                        return (
                                            <TouchableOpacity
                                                key={p.key}
                                                style={[styles.tab, isActive && styles.tabActive]}
                                                onPress={() => setSheetPeriod(p.key)}
                                                activeOpacity={0.7}
                                            >
                                                <Text style={[styles.tabText, isActive && styles.tabTextActive]}>
                                                    {p.label}
                                                </Text>
                                            </TouchableOpacity>
                                        );
                                    })}
                                </View>

                                <Text style={styles.sheetSectionLabel}>Date Range</Text>
                                <DatePickerInput
                                    label="From Date"
                                    value={sheetFromDate}
                                    onChangeDate={setSheetFromDate}
                                    placeholder="Select start date"
                                />
                                <DatePickerInput
                                    label="To Date"
                                    value={sheetToDate}
                                    onChangeDate={setSheetToDate}
                                    placeholder="Select end date"
                                />

                                <View style={styles.sheetActions}>
                                    <TouchableOpacity
                                        style={styles.sheetCancel}
                                        onPress={() => setFilterVisible(false)}
                                        activeOpacity={0.7}
                                    >
                                        <Text style={styles.sheetCancelText}>Cancel</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={[styles.sheetApply, applyingFilter && styles.sheetApplyDisabled]}
                                        onPress={applyFilterSheet}
                                        activeOpacity={0.7}
                                        disabled={applyingFilter}
                                    >
                                        {applyingFilter ? (
                                            <ActivityIndicator size="small" color={theme.colors.surface} />
                                        ) : (
                                            <>
                                                <Icon name="check" size={18} color={theme.colors.surface} />
                                                <Text style={styles.sheetApplyText}>Apply</Text>
                                            </>
                                        )}
                                    </TouchableOpacity>
                                </View>
                            </View>
                        </TouchableWithoutFeedback>
                    </View>
                </TouchableWithoutFeedback>
            </Modal>
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    header: {
        backgroundColor: theme.colors.primary,
        paddingVertical: theme.spacing.sm,
        paddingHorizontal: theme.spacing.md,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...theme.shadows.sm,
    },
    title: {
        ...theme.typography.h4,
        color: theme.colors.surface,
        fontWeight: '700',
        textAlign: 'center',
    },
    headerPlaceholder: {
        width: 32,
        height: 32,
    },
    headerAction: {
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.borderRadius.md,
    },
    // Tab Section
    tabWrapper: {
        flexDirection: 'row',
        paddingHorizontal: theme.spacing.md,
        paddingTop: theme.spacing.md,
        gap: theme.spacing.sm,
    },
    tab: {
        flex: 1,
        paddingVertical: theme.spacing.sm,
        alignItems: 'center',
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    tabActive: {
        backgroundColor: theme.colors.primary,
        borderColor: theme.colors.primary,
    },
    tabText: {
        ...theme.typography.caption,
        color: theme.colors.text,
        fontWeight: '600',
    },
    tabTextActive: {
        color: theme.colors.surface,
    },
    // Date Selector
    dateSelectorWrapper: {
        paddingHorizontal: theme.spacing.md,
        marginTop: theme.spacing.md,
    },
    dateSelectorCard: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.sm,
        ...theme.shadows.sm,
    },
    dateSelector: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.md,
        paddingHorizontal: theme.spacing.md,
        gap: theme.spacing.sm,
    },
    dateNavButton: {
        width: 42,
        height: 42,
        borderRadius: theme.borderRadius.round,
        backgroundColor: theme.colors.primary + '15',
        justifyContent: 'center',
        alignItems: 'center',
    },
    dateDisplay: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        gap: theme.spacing.sm,
    },
    dateIconContainer: {
        width: 32,
        height: 32,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.primary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    calendarIcon: {
        marginRight: theme.spacing.sm,
    },
    dateText: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
    },
    todayButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        gap: theme.spacing.xs,
    },
    todayButtonText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    // Range info pill
    rangeInfoWrapper: {
        marginHorizontal: theme.spacing.md,
        marginTop: theme.spacing.sm,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        borderWidth: 1,
        borderColor: theme.colors.border,
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.xs,
        ...theme.shadows.sm,
    },
    rangeInfoText: {
        ...theme.typography.caption,
        color: theme.colors.text,
        fontWeight: '600',
    },
    // Bottom Sheet styles
    sheetOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.3)',
        justifyContent: 'flex-end',
        ...(Platform.OS === 'web' && {
            width: '100%',
            maxWidth: 550,
            alignSelf: 'center',
        }),
    },
    bottomSheet: {
        backgroundColor: theme.colors.surface,
        borderTopLeftRadius: theme.borderRadius.xl,
        borderTopRightRadius: theme.borderRadius.xl,
        paddingHorizontal: theme.spacing.md,
        paddingTop: theme.spacing.md,
        paddingBottom: theme.spacing.lg,
        ...(Platform.OS === 'web' && {
            width: '100%',
            maxWidth: 550,
            alignSelf: 'center',
        }),
        ...theme.shadows.lg,
    },
    sheetHandle: {
        alignSelf: 'center',
        width: 48,
        height: 4,
        borderRadius: 2,
        backgroundColor: theme.colors.border,
        marginBottom: theme.spacing.sm,
    },
    sheetTitle: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '700',
        textAlign: 'center',
        marginBottom: theme.spacing.md,
    },
    sheetSectionLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        fontWeight: '600',
        marginBottom: theme.spacing.sm,
        marginTop: theme.spacing.sm,
    },
    sheetTabsRow: {
        flexDirection: 'row',
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.md,
    },
    sheetActions: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.md,
    },
    sheetCancel: {
        flex: 1,
       height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.background,
        alignItems: 'center',
        justifyContent: 'center',
    },
    sheetCancelText: {
        ...theme.typography.body1,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    sheetApply: {
        flex: 1,
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: theme.spacing.xs,
    },
    sheetApplyDisabled: {
        opacity: 0.7,
    },
    sheetApplyText: {
        ...theme.typography.body1,
        color: theme.colors.surface,
        fontWeight: '600',
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        paddingHorizontal: theme.spacing.md,
        paddingTop: theme.spacing.md,
    },
    // Dashboard-style Cards
    card: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    cardTitle: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
        marginBottom: theme.spacing.md,
    },
    cardRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    cardItem: {
        flex: 1,
        alignItems: 'center',
    },
    cardIconContainer: {
        width: 44,
        height: 44,
        borderRadius: 22,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    cardLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginBottom: 4,
    },
    cardValue: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '700',
    },
    cardDivider: {
        width: 1,
        height: 70,
        backgroundColor: theme.colors.divider,
        marginHorizontal: theme.spacing.md,
    },
    // List-style items (like Dashboard expenses)
    listItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
    },
    listIconContainer: {
        width: 36,
        height: 36,
        borderRadius: theme.borderRadius.md,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    listLabel: {
        ...theme.typography.body2,
        color: theme.colors.text,
        flex: 1,
    },
    listValue: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
    },
    listValueContainer: {
        alignItems: 'flex-end',
    },
    listSubValue: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    listDivider: {
        height: 1,
        backgroundColor: theme.colors.divider,
    },
    section: {
        marginBottom: theme.spacing.lg,
    },
    sectionTitle: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '700',
        marginBottom: theme.spacing.sm,
    },
    statsRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
    },
    statCard: {
        flex: 1,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        alignItems: 'center',
        ...theme.shadows.sm,
    },
    statCardPrimary: {
        borderLeftWidth: 4,
        borderLeftColor: theme.colors.primary,
    },
    statCardSecondary: {
        borderLeftWidth: 4,
        borderLeftColor: theme.colors.success,
    },
    statCardWide: {
        flex: 1,
    },
    statValue: {
        ...theme.typography.h2,
        color: theme.colors.primary,
        fontWeight: '700',
    },
    statUnit: {
        ...theme.typography.body1,
        color: theme.colors.primary,
        fontWeight: '600',
        marginTop: -4,
    },
    statLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.xs,
    },
    profitRow: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
    },
    profitItem: {
        flex: 1,
        alignItems: 'center',
    },
    profitDivider: {
        width: 1,
        height: 50,
        backgroundColor: theme.colors.border,
    },
    profitValue: {
        ...theme.typography.h3,
        fontWeight: '700',
    },
    profitLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.xs,
    },
    expenseCard: {
        flex: 1,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        alignItems: 'center',
        ...theme.shadows.sm,
    },
    // Dairy Records Section
    dairyCard: {
        flex: 1,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        alignItems: 'center',
        ...theme.shadows.sm,
    },
    dairyIconBg: {
        width: 44,
        height: 44,
        borderRadius: 22,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    dairyLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginBottom: 4,
    },
    dairyQty: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '700',
    },
    dairyAmountText: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    expenseValue: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '700',
        marginTop: theme.spacing.xs,
    },
    expenseLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    cowCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        ...theme.shadows.sm,
    },
    cowHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    cowInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
    },
    cowName: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '600',
    },
    cowMilk: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 2,
    },
    cowMilkValue: {
        ...theme.typography.h5,
        color: theme.colors.primary,
        fontWeight: '700',
    },
    cowMilkUnit: {
        ...theme.typography.caption,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    cowStats: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing.sm,
        paddingTop: theme.spacing.sm,
        borderTopWidth: 1,
        borderTopColor: theme.colors.divider,
    },
    cowStatItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.xs,
    },
    cowStatLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    cowStatValue: {
        ...theme.typography.body2,
        color: theme.colors.text,
        fontWeight: '600',
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.xxl * 2,
    },
    emptyTitle: {
        ...theme.typography.h5,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.md,
    },
    emptySubtitle: {
        ...theme.typography.body2,
        color: theme.colors.textLight,
        textAlign: 'center',
        marginTop: theme.spacing.sm,
        lineHeight: 22,
    },
    loadingContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.xxl,
    },
    loadingText: {
        ...theme.typography.body1,
        color: theme.colors.textSecondary,
    },
    bottomPadding: {
        height: theme.spacing.xl,
    },
    // Net Profit Card Styles
    netProfitCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.md,
        borderWidth: 1,
        borderColor: theme.colors.divider,
    },
    netProfitHeader: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    netProfitIconContainer: {
        width: 48,
        height: 48,
        borderRadius: 24,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    netProfitContent: {
        flex: 1,
    },
    netProfitLabel: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
    },
    netProfitSubLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    netProfitValue: {
        ...theme.typography.h5,
        fontWeight: '800',
    },
    netProfitBreakdown: {
        flexDirection: 'row',
        marginTop: theme.spacing.md,
        paddingTop: theme.spacing.md,
        borderTopWidth: 1,
        borderTopColor: theme.colors.divider,
    },
    netProfitBreakdownItem: {
        flex: 1,
        alignItems: 'center',
    },
    netProfitBreakdownDivider: {
        width: 1,
        height: 30,
        backgroundColor: theme.colors.divider,
    },
    breakdownLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginBottom: 4,
    },
    breakdownValue: {
        ...theme.typography.body2,
        fontWeight: '700',
    },
});

export default ReportsScreen;
